<template>
    <v-container class="py-3 px-3" v-if="!totalsHidden">
        <v-row class="pa-0 ma-0" justify="center">
            <v-col
                v-for="(field, index) in visibleFields"
                :key="index"
                class="py-0 totals-col"
            >
                <v-card class="background-2" elevation="0">
                    <v-card-text class="text-center pa-0">
                        <v-icon :color="field.color" size="28" class="mb-1">{{ field.icon }}</v-icon>
                        <div :class="field.labelClass || 'text-caption'">{{ field.text }}</div>
                        <div
                            style="font-size: 16px !important"
                            class="text-h6 font-weight-bold"
                            :class="getTextColorClass(field.color)"
                        >
                            {{ formatValue(field) }}
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>

export default {
    name: 'footer-product-document-table',
    props: {
        type: {
            type: String,
            default: 'edit'
        },
        items: Array,
        relationsLoaded: Object,
        parent: Object,
        fieldsset: Array,
        totalsFields: {
            type: Array,
            default: null
        },
        totalsHidden: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            // Definizione di default di tutti i campi disponibili
            defaultFields: [
                {
                    text: 'Imponibile',
                    key: 'taxable_gross',
                    icon: 'mdi-receipt-text-outline',
                    type: 'currency',
                    color: 'cyan',
                },
                {
                    text: 'Sconto',
                    key: 'global_discount_total',
                    icon: 'mdi-percent-outline',
                    type: 'currency',
                    color: 'orange darken-2',
                },
                {
                    text: 'Imp. Scontato',
                    key: 'subtotal',
                    icon: 'mdi-cash-multiple',
                    type: 'currency',
                    color: 'blue',
                },
                {
                    text: 'Iva',
                    key: 'tax',
                    icon: 'mdi-account-cash-outline',
                    type: 'currency',
                    color: 'orange darken-1',
                },
                {
                    text: 'Tot. a Pagare',
                    key: 'total_to_pay',
                    icon: 'mdi-credit-card-outline',
                    type: 'currency',
                    color: 'teal darken-2',
                    condition: () => this.rowsAmount > 0
                },
                {
                    text: 'Costo',
                    key: 'cost',
                    icon: 'mdi-cart-arrow-down',
                    type: 'currency',
                    color: 'red',
                    labelClass: 'text-caption font-weight-medium',
                    condition: () => (this.type == 'show' && this.parent && this.parent.cost !== undefined) || this.type == 'edit'
                },
                {
                    text: 'Margine',
                    key: 'margin_percent',
                    icon: 'mdi-chart-line',
                    type: 'percentage',
                    color: 'deep-purple',
                    labelClass: 'text-caption font-weight-medium',
                    condition: () => (this.type == 'show' && this.parent && this.parent.cost !== undefined) || this.type == 'edit'
                },
                {
                    text: 'Totale Doc.',
                    key: 'total',
                    icon: 'mdi-cash-check',
                    type: 'currency',
                    color: 'green',
                    labelClass: 'text-caption font-weight-medium',
                },
            ]
        }
    },
    methods: {
        isLayoutElement(item) {
            if (!item || typeof item !== 'object') return false
            return ['description', 'separator', 'pagebreak', 'subtotal'].includes(item.type)
        },

        calcNetPrice(item, discount = true) {
            // Skip layout elements (separator/description/pagebreak)
            if (this.isLayoutElement(item)) return 0

            // Per prodotti percentuali, calcola in base all'imponibile base
            if (item.type === 'percentage') {
                // Se price_locked E il price è valorizzato, usa il prezzo salvato
                if (item.price_locked && item.price > 0) {
                    return Math.round((item.price + Number.EPSILON) * 100) / 100
                }
                // Altrimenti calcola in tempo reale: quantity contiene la percentuale
                const percentage = item.quantity || 0
                return Math.round(((this.baseSubtotal * percentage / 100) + Number.EPSILON) * 100) / 100
            }

            let price = item.price
            let quantity_per_pack = typeof item.quantity_per_pack !== 'undefined' ? item.quantity_per_pack : 1
            if(discount) {
                // Apply line discounts for all items regardless of type
                for(let j = 1; j <= 5; j++) {
                    if(typeof item['discount'+j] === 'number') {
                        price = price * (100 - item['discount'+j]) / 100
                    }
                }

                // Apply global discount only if not an expense item or the setting is disabled
                if (!(this.excludeDiscountFromExpense && item.type === 'spesa')) {
                    price = price * (100 - this.globalDiscount) / 100
                }
            }
            return Math.round(((price * quantity_per_pack * item.quantity) + Number.EPSILON) * 100) / 100
        },

        formatValue(field) {
            const value = this.totalsData[field.key]
            if (value === null || value === undefined) {
                return '-'
            }

            switch (field.type) {
                case 'currency':
                    return this.$options.filters.currency(value, 2, this.currency)
                case 'number':
                    return value.toLocaleString('it-IT')
                case 'percentage':
                    return value.toLocaleString('it-IT', { maximumFractionDigits: 2 }) + '%'
                default:
                    return value
            }
        },

        getTextColorClass(color) {
            if (!color) return ''
            const parts = color.split(' ')
            if (parts.length > 1) {
                return `${parts[0]}--text text--${parts[1]}`
            }
            return `${color}--text`
        }
    },
    computed: {
        // Esclude entry null/undefined (transitorie durante render reattivi / splice) prima dei calcoli totali
        validItems() {
            return (this.items || []).filter(item => item != null)
        },

        // Add new computed property to check if expense items should be excluded from global discount
        excludeDiscountFromExpense() {
            return this.$store.getters.checkSettings('exclude_discount_from_expense')
        },

        // Imponibile base per calcolo prodotti percentuali (esclude description e percentage)
        baseSubtotal() {
            let total = 0
            _.forEach(this.validItems, (item) => {
                if (!this.isLayoutElement(item) && item.type !== 'percentage') {
                    total = total + this.calcNetPrice(item)
                }
            })
            return total
        },

        currency() {
            return typeof this.parent.currency != 'undefined' ? this.parent.currency : 'EUR'
        },

        totalDiscount() {
            let total = 0
            _.forEach(this.validItems, (item, i) => {
                if(!this.isLayoutElement(item)) {
                    total = total + this.calcNetPrice(item, false)
                }
            })
            return total - this.taxable
        },

        taxable() {
            if(this.type == 'show') {
                return this.parent.subtotal
            }
            let totalTaxable = 0
            _.forEach(this.validItems, (item, i) => {
                if(!this.isLayoutElement(item)) {
                    totalTaxable = totalTaxable + this.calcNetPrice(item)
                }
            })
            return totalTaxable
        },

        calcGlobalDiscount() {
            if(this.globalDiscount === 0) {
                return 0
            } else {
                return this.taxable * (this.globalDiscount / 100)
            }
        },

        vat() {
            if(this.type == 'show') {
                return this.parent.tax
            }
            let totalVat = 0
            if (this.fieldsset) {
                let fieldIndex = _.findIndex(this.fieldsset, function (o) {
                    return o.key === 'vat'
                });
                // Nessun campo "vat" nel fieldset (es. nascosto da configurazione): niente IVA da calcolare
                if (fieldIndex === -1 || !this.fieldsset[fieldIndex].options) {
                    return 0
                }
                let vatOptions = this.fieldsset[fieldIndex].options
                _.forEach(this.validItems, (item, i) => {
                    if(!this.isLayoutElement(item)) {
                        let optionIndex = _.findIndex(vatOptions, (o) => {
                            return o.value === item.vat
                        })
                        if (optionIndex === -1 || !vatOptions[optionIndex].extra_fields) {
                            return
                        }
                        totalVat = totalVat + this.calcNetPrice(item) * vatOptions[optionIndex].extra_fields.vat / 100
                    }
                })
            }
            return Math.round(totalVat * 100) / 100
        },

        globalDiscount() {
            if(this.parent && typeof this.parent.global_discount === 'number') {
                return this.parent.global_discount
            } else {
                return 0
            }
        },

        total() {
            return this.taxable + this.vat - this.rowsAmount
        },

        rows() {
            if (!this.relationsLoaded || !this.relationsLoaded['rows'] || !this.relationsLoaded['rows']['collection']) return []
            return this.relationsLoaded['rows']['collection']
        },

        rowsAmount() {
            if (!this.rows) return 0
            return this.rows.reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0)
        },

        cost() {
            if(this.type == 'show') {
                return this.parent.cost
            }
            let totalCost = 0
            _.forEach(this.validItems, (item, i) => {
                if(!this.isLayoutElement(item)) {
                    totalCost = totalCost + (item.purchase_price ? item.purchase_price * item.quantity : 0)
                }
            })
            return Math.round(totalCost * 100) / 100
        },

        // Oggetto con tutti i valori calcolati
        totalsData() {
            return {
                taxable_gross: this.taxable + this.totalDiscount,
                global_discount_total: this.totalDiscount,
                subtotal: this.taxable,
                tax: this.vat,
                total_to_pay: this.total,
                cost: this.cost,
                margin_percent: this.taxable > 0 ? Math.round(((this.taxable - this.cost) / this.taxable * 100) * 100) / 100 : 0,
                total: this.total + this.rowsAmount
            }
        },

        // Campi da visualizzare
        visibleFields() {
            // Se la sezione è nascosta, non mostrare nulla
            if (this.totalsHidden) {
                return []
            }

            let fieldsToShow = []

            // Se c'e' una configurazione dal backend, usa quella
            if (this.totalsFields && this.totalsFields.length > 0) {
                // Mappa i campi configurati con le definizioni di default per ottenere icone, colori, ecc.
                this.totalsFields.forEach(configField => {
                    // Cerca il campo corrispondente nei default
                    let defaultField = this.defaultFields.find(f => f.key === configField.key)
                    if (defaultField) {
                        // Usa i valori dal backend se presenti, altrimenti usa i default
                        let field = {
                            ...defaultField,
                            text: configField.text || defaultField.text,
                            icon: configField.icon || defaultField.icon,
                            color: configField.color || defaultField.color
                        }
                        // Verifica la condizione se presente
                        if (!field.condition || field.condition()) {
                            fieldsToShow.push(field)
                        }
                    }
                })
            } else {
                // Nessuna configurazione: mostra tutti i campi di default
                this.defaultFields.forEach(field => {
                    if (!field.condition || field.condition()) {
                        fieldsToShow.push(field)
                    }
                })
            }

            return fieldsToShow
        }
    },

}
</script>

<style scoped>
.totals-col {
    flex: 1 1 0;
    min-width: 110px;
}
</style>
